import { ChangeEvent, FC, useCallback, useContext, useEffect, useState } from 'react';
import { css } from '@emotion/css';
import colors from '@prezzee/ribbon-ui/lib/theme/foundations/colors';
import Typography from '@prezzee/ribbon-ui/lib/components/Typography';
import { assign, cloneDeep, compact, eq, find, isUndefined } from 'lodash';

import { DrainContext, FilterCurrent, FilterCurrentElement } from 'contexts/DrainContext';
import { EMPTY_ELEMENT } from 'common/utils/react';
import { EMPTY_STRING } from 'common/utils/string';
import { FilterTaxonomyOptions } from 'api/queries/filter';

import { ItemType } from './SelectFilter';
import SelectFilterCheckboxGroup from './SelectFilterCheckboxGroup';
import SelectFilterSearch from './SelectFilterSearch';

const Styles = {
  container: (isOpen, isFourColumns, popupRight) => css`
    position: absolute;
    top: 60px;
    ${popupRight ? `right: 0;` : `left: 0;`}
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.2);
    padding: 16px 0 0 0;
    z-index: 21;
    width: ${isFourColumns ? '440px' : '334px'};
    opacity: ${isOpen ? '1' : '0'};
    visibility: ${isOpen ? 'visible' : 'hidden'};
    transition: visibility 200ms linear, opacity 150ms linear;
  `,

  textButton: css`
    color: ${colors.purple[400]};
  `,

  titleContainer: css`
    display: flex;
    justify-content: space-between;
    padding: 0 16px;
  `,

  bottonContorls: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 12px 16px 0;
    border-top: 1px solid #d9d9d9;
  `,

  clearButton: css`
    margin-right: 30px;
  `,

  checkboxGroup: css`
    display: flex;
    flex-direction: column;
  `,

  checkbox: css``,
};

const SEARCH_FILTER_ITEM_COUNT = 8;

interface Props {
  filterOptions: FilterTaxonomyOptions;
  items: ItemType[];
  title: string;
  isOpen: boolean;
  iconsGrid: boolean;
  popupRight: boolean;
  searchLabel: string;
  closePopup: () => void;
  onApply: (filter: FilterCurrent, activeItems: ItemType[]) => void;
  parentCodename?: string;
  isSearchBar: boolean;
}

const SelectFilterPopup: FC<Props> = ({
  filterOptions,
  items,
  title,
  isOpen,
  iconsGrid,
  popupRight = false,
  searchLabel,
  closePopup,
  onApply,
  parentCodename,
  isSearchBar,
}) => {
  const drain = useContext(DrainContext);

  const [itemsStatus, setItemsStatus] = useState(
    Object.fromEntries(
      items.map(({ codename }) => [
        codename,
        !isUndefined(
          drain.filter.elements[
            drain.filter.elements.findIndex((value: FilterCurrentElement) => value.codename === codename)
          ]
        ) || !isUndefined(find(drain.currentValue.filter.elements, ({ codename: cn }) => eq(codename, cn))),
      ])
    )
  );

  const [prevItemList, setPrevItemList] = useState(itemsStatus);
  const [search, setSearch] = useState(EMPTY_STRING);

  const isFourColumns = iconsGrid && items.length > 9;

  const isSearchVisible = isSearchBar && !iconsGrid && items.length > SEARCH_FILTER_ITEM_COUNT;

  const [filterList, setFilterList] = useState<ItemType[]>(items);

  useEffect(() => {
    setFilterList(
      items.filter(({ name }: ItemType) => {
        const value = search.toLocaleLowerCase();
        return name.search(new RegExp(value, 'gi')) !== -1;
      })
    );
  }, [search]);

  const clear = () => {
    const itemStatusList = Object.fromEntries(items.map(({ codename }) => [codename, false]));
    setItemsStatus(itemStatusList);
    const itemList = items.filter(item => itemStatusList[item.codename]);
    drain.clearFilter(parentCodename);
    onApply(drain.filter, itemList);

    closePopup();
  };

  const onCancelClick = () => {
    closePopup();
  };

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, codename: string, name: string) => {
      const checked = e.target.checked;
      setItemsStatus({ ...itemsStatus, [codename]: checked });
      onApplyClick(name, codename, checked);
    },
    [drain.filter, drain.sort, drain.search]
  );

  const onApplyClick = useCallback(
    (name: string, codename: string, checked: boolean) => {
      const checkedItems = { ...itemsStatus, [codename]: checked };
      const itemList = items.filter(item => checkedItems[item.codename]);
      setPrevItemList(itemsStatus);

      const filter = drain.filter.elements.map(({ codename }) => codename).includes(codename)
        ? cloneDeep(
            assign(
              {},
              {
                elements: [...drain.filter.elements.filter(({ codename: cn }) => cn !== codename)],
              }
            )
          )
        : cloneDeep(
            assign(
              {},
              {
                elements: compact([
                  ...drain.filter.elements,
                  {
                    codename,
                    name,
                    parent: {
                      parent: void 0,
                      name: title,
                      codename: parentCodename,
                    },
                  },
                ]),
              }
            )
          );

      onApply(filter, itemList);
    },
    [drain.filter, drain.search, drain.sort, prevItemList, itemsStatus]
  );

  const onSearchChange = (value: string) => {
    setSearch(value);
  };

  return (
    <div className={Styles.container(isOpen, isFourColumns, popupRight)}>
      <div className={Styles.titleContainer}>
        <Typography variant={'body2'} as={'h3'}>
          {title}
        </Typography>
        <button className={Styles.textButton} onClick={clear}>
          <Typography variant={'body2'} as={'span'}>
            {filterOptions.root.clearAll.title}
          </Typography>
        </button>
      </div>
      {isSearchVisible ? <SelectFilterSearch placeholder={searchLabel} handleSearchChange={onSearchChange} /> : null}
      {iconsGrid ? (
        EMPTY_ELEMENT
      ) : (
        <SelectFilterCheckboxGroup
          items={filterList}
          itemsStatus={itemsStatus}
          onChange={onChange}
          parentCodename={parentCodename}
          title={title}
        />
      )}
    </div>
  );
};

export default SelectFilterPopup;
